import { FunnelIcon } from "@heroicons/react/20/solid";
import { OtsActionButton } from "../components/OtsButton";
import { OtsMultiSelect } from "../components/OtsMultiSelect";
import { OtsPopover } from "../components/OtsPopover";
import {
  ACTIVITY_FACET_KEYS,
  ACTIVITY_PARTY_ROLES,
  activeFacetCount,
  type ActivityFacetKey,
  type ActivityFacetOptions,
  type ActivityFacets,
  type ActivityPartyRole,
} from "./driver-activity-view-model";

export type DriverActivityFiltersProps = {
  value: ActivityFacets;
  onChange: (value: ActivityFacets) => void;
  /** What the day can be narrowed to. */
  options: ActivityFacetOptions;
  /** The kinds of filter offered; all of them by default. */
  facetKeys?: readonly ActivityFacetKey[];
};

/**
 * Narrows the day to some drivers, trucks, trailers, and companies by the part they play on the ticket (from its
 * parties): the responsible provider, provider, operator, disposal company, or marketer. Every field is always in
 * the same place; one the day has nothing for is disabled. Within a facet any pick matches; every facet with picks must.
 */
const heading = "mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--ots-text-muted)]";

export const DriverActivityFilters = ({ value, onChange, options, facetKeys = ACTIVITY_FACET_KEYS }: DriverActivityFiltersProps) => {
  const active = activeFacetCount(value);
  const setParty = (role: ActivityPartyRole, companyIds: string[]) =>
    onChange({ ...value, parties: { ...value.parties, [role]: companyIds.length ? companyIds : undefined } });
  const fields = ([
    { facet: "person", key: "personIds", label: "Driver", options: options.person },
    { facet: "vehicle", key: "vehicleIds", label: "Truck", options: options.vehicle },
    { facet: "trailer", key: "trailerIds", label: "Trailer", options: options.trailer },
  ] as const).filter(({ facet }) => facetKeys.includes(facet));
  const companies = facetKeys.includes("parties");

  return (
    <OtsPopover
      density="compact"
      placement="bottom end"
      ariaLabel={active ? `Filters, ${active} on` : "Filters"}
      tooltip={companies ? "Filter by driver, equipment, or company" : "Filter by driver or equipment"}
      pressed={active > 0}
      buttonClassName={[
        "inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-semibold outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]",
        active ? "bg-[var(--ots-primary-container)] text-[var(--ots-on-primary-container)]" : "text-[var(--ots-primary)] hover:bg-[var(--ots-primary-container)]",
      ].join(" ")}
      allowOverflow
      // The display options' shape: two columns, people and equipment beside companies, nothing to scroll.
      panelClassName={companies
        ? "grid w-[36rem] !max-w-[calc(100vw-2rem)] grid-cols-1 gap-x-6 gap-y-3 !p-3 sm:grid-cols-2"
        : "grid w-72 !max-w-[calc(100vw-2rem)] grid-cols-1 gap-y-3 !p-3"}
      trigger={<>
        <FunnelIcon aria-hidden="true" className="size-4"/>
        {active > 0 && <span className="tabular-nums">{active}</span>}
      </>}>
      <fieldset data-filter-column="people" className="!m-0 flex min-w-0 flex-col gap-3 !border-0 !p-0">
        <legend className={heading}>Driver and equipment</legend>
        {fields.map((field) => (
          <OtsMultiSelect
            key={field.key}
            label={field.label}
            size="compact"
            options={field.options}
            disabled={!field.options.length}
            placeholder={field.options.length ? undefined : `No ${field.label.toLowerCase()}s today`}
            value={value[field.key] ?? []}
            onChange={(ids) => onChange({ ...value, [field.key]: ids.length ? ids : undefined })}/>
        ))}
      </fieldset>
      {companies && <fieldset data-filter-column="companies" className="!m-0 flex min-w-0 flex-col gap-3 !border-0 !p-0">
        <legend className={heading}>Companies</legend>
        {ACTIVITY_PARTY_ROLES.map(({ key, label }) => (
          <OtsMultiSelect
            key={key}
            label={label}
            size="compact"
            options={options.parties[key]}
            disabled={!options.parties[key].length}
            placeholder={options.parties[key].length ? undefined : "None on today's tickets"}
            value={value.parties?.[key] ?? []}
            onChange={(ids) => setParty(key, ids)}/>
        ))}
      </fieldset>}
      <div className={companies ? "flex justify-end sm:col-span-2" : "flex justify-end"}>
        <OtsActionButton size="compact" variant="text" disabled={!active} onClick={() => onChange({})}>Clear filters</OtsActionButton>
      </div>
    </OtsPopover>
  );
};
